// MAIN JS
document.addEventListener('alpine:init', () => {
  Alpine.store('megaMenu', {
    isOpen: false,
    activeMenu: '',
    shouldTrapFocus: false,

    toggle() {
      this.isOpen = !this.isOpen;
      if (!this.isOpen) {
        this.shouldTrapFocus = false;
      }
    },

    check(event) {
      // The outside listener lives on the always-rendered global nav, so it
      // fires on every page click. Only ever close an already-open menu here —
      // never toggle, or an unrelated click would re-open the last active menu.
      if (!this.isOpen) {
        return;
      }

      // Ignore the trigger buttons (they manage open/close themselves) and any
      // click inside a mega-menu panel (the active brand panel sits outside the
      // global nav this handler is bound to).
      if (event.target.closest('[data-menu-trigger]') || event.target.closest('.c-mega-menu')) {
        return;
      }

      this.isOpen = false;
      this.shouldTrapFocus = false;
    },

    updateActiveMenu(menu) {
      if (this.activeMenu === menu && this.isOpen) {
        // If current menu matches selection and navigation is open, close it
        this.toggle();
      } else if (this.isOpen && this.activeMenu !== menu) {
        // If navigation is open but menu doesn't match, change active menu
        this.activeMenu = menu;
        this.shouldTrapFocus = false; // Reset focus trap when switching menus
      } else {
        // If navigation is closed, open it and set active menu
        this.activeMenu = menu;
        this.toggle();
      }
    },

    handleToggleKeydown(event, menuType) {
      // Only intercept Tab when menu is expanded and matches the menu type
      if (event.key === 'Tab' && !event.shiftKey && this.isOpen && this.activeMenu === menuType) {
        event.preventDefault();
        // Find first focusable element in the visible mega menu
        const firstMenuItem = document.querySelector('.c-mega-menu.is-visible [tabindex="0"], .c-mega-menu.is-visible button, .c-mega-menu.is-visible a');
        if (firstMenuItem) {
          firstMenuItem.focus();
          this.shouldTrapFocus = true; // Activate trap once in menu
        }
      }
    },

    closeMenu() {
      if (this.isOpen) {
        const currentActiveMenu = this.activeMenu;
        
        // Close the menu first
        this.isOpen = false;
        this.shouldTrapFocus = false;
        
        // Find and focus the appropriate toggle button
        setTimeout(() => {
          let toggleButton = null;
          
          if (currentActiveMenu === 'global') {
            // Desktop main menu button
            toggleButton = document.querySelector('.c-header__burger--desktop[data-menu-trigger]');
          } else {
            // Brand menu button - find by matching the click handler content
            const brandButtons = document.querySelectorAll('.o-btn[data-menu-trigger]');
            for (const button of brandButtons) {
              const clickHandler = button.getAttribute('@click');
              if (clickHandler && clickHandler.includes(`'${currentActiveMenu}'`)) {
                toggleButton = button;
                break;
              }
            }
          }
          
          if (toggleButton) {
            toggleButton.focus();
          }
        }, 50); // Small delay to ensure menu is closed before focusing
      }
    }
  });

  Alpine.store('search', {
    isOpen: false,
    terms: '',
    suggestions: [],
    selectedIndex: -1,
    loading: false,
    debounceTimer: null,

    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.selectedIndex = -1;
        // Focus the input after a brief delay to ensure it's rendered
        setTimeout(() => {
          const input = document.querySelector('.c-search__input');
          if (input) input.focus();
        }, 100);
      } else {
        this.clearSuggestions();
        this.terms = '';
      }
    },

    clearSuggestions() {
      this.suggestions = [];
      this.selectedIndex = -1;
    },

    async fetchSuggestions(query) {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      if (query.length < 2) {
        this.clearSuggestions();
        return;
      }

      this.loading = true;

      try {
        const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=5`);
        const data = await response.json();
        
        const suggestions = [];
        
        // Process products
        if (data.resources.results.products) {
          data.resources.results.products.forEach(product => {
            const breadcrumb = [];
            if (product.vendor) breadcrumb.push(product.vendor);
            if (product.product_type) breadcrumb.push(product.product_type);
            
            suggestions.push({
              title: product.title,
              url: product.url,
              type: 'product',
              breadcrumb: breadcrumb.length > 0 ? breadcrumb : null
            });
          });
        }
        
        // Process collections
        if (data.resources.results.collections) {
          data.resources.results.collections.forEach(collection => {
            suggestions.push({
              title: collection.title,
              url: collection.url,
              type: 'collection',
              breadcrumb: ['Collection']
            });
          });
        }
        
        // Process articles
        if (data.resources.results.articles) {
          data.resources.results.articles.forEach(article => {
            const breadcrumb = [];
            if (article.blog_title) breadcrumb.push(article.blog_title);
            breadcrumb.push('Blog');
            
            suggestions.push({
              title: article.title,
              url: article.url,
              type: 'article',
              breadcrumb: breadcrumb
            });
          });
        }
        
        this.suggestions = suggestions;
      } catch (error) {
        console.error('Search suggestions fetch failed:', error);
        this.clearSuggestions();
      } finally {
        this.loading = false;
      }
    },

    navigateDown() {
      if (this.suggestions.length === 0) return;
      this.selectedIndex = this.selectedIndex < this.suggestions.length - 1 
        ? this.selectedIndex + 1 
        : 0;
    },

    navigateUp() {
      if (this.suggestions.length === 0) return;
      this.selectedIndex = this.selectedIndex > 0 
        ? this.selectedIndex - 1 
        : -1;
    }
  });

  Alpine.store('sizeGuide', {
    isOpen: false,
    currentUnit: 'inches',

    toggle() {
      this.isOpen = !this.isOpen;
    },

    open() {
      this.isOpen = true;
    },

    close() {
      this.isOpen = false;
    },

    toggleUnit() {
      this.currentUnit = this.currentUnit === 'inches' ? 'cm' : 'inches';
    }
  });

  Alpine.store('cart', {
    itemCount: window.cartItemCount || 0,
    previousCount: window.cartItemCount || 0,
    animating: false,
    direction: 'none', // 'up', 'down', or 'none'
    statusMessage: '',
    focusTargetKey: '',
    focusTargetAction: '',
    focusTargetButton: '',

    updateCount(count) {
      this.previousCount = this.itemCount;
      this.itemCount = count;
      
      // Determine animation direction
      if (count > this.previousCount) {
        this.direction = 'up';
        this.animating = true;
      } else if (count < this.previousCount) {
        this.direction = 'down';
        this.animating = true;
      } else {
        this.direction = 'none';
        this.animating = false;
      }
      
      // Reset animation state after animation completes
      if (this.animating) {
        setTimeout(() => {
          this.animating = false;
          this.direction = 'none';
        }, 500);
      }
    },

    updateStatusMessage(message) {
      this.statusMessage = message;
      
      // Clear status message after announcement to prevent repetition
      if (message) {
        setTimeout(() => {
          this.statusMessage = '';
        }, 2000);
      }
    },

    setFocusTarget(key, action, button = '') {
      this.focusTargetKey = key;
      this.focusTargetAction = action;
      this.focusTargetButton = button;
    },

    clearFocusTarget() {
      this.focusTargetKey = '';
      this.focusTargetAction = '';
      this.focusTargetButton = '';
    }
  });

  Alpine.store('cartDrawer', {
    isOpen: false,

    toggle() {
      this.isOpen = !this.isOpen;
    },

    open() {
      this.isOpen = true;
    },

    close() {
      this.isOpen = false;
    }
  });

  Alpine.store('mobileMenu', {
    isOpen: false,
    currentLevel: 'main',
    navigationStack: [],

    open() {
      this.isOpen = true;
      this.currentLevel = 'main';
      this.navigationStack = [];
    },

    close() {
      this.isOpen = false;
      this.currentLevel = 'main';
      this.navigationStack = [];
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    navigate(menuKey, menuTitle, menuType = 'menu') {
      this.navigationStack.push({
        level: this.currentLevel,
        title: menuTitle,
        type: menuType
      });
      this.currentLevel = menuKey;
    },

    navigateToTertiary(brandKey, columnKey, columnTitle) {
      const tertiaryKey = `${brandKey}__${columnKey}`;
      this.navigationStack.push({
        level: this.currentLevel,
        title: columnTitle,
        type: 'tertiary'
      });
      this.currentLevel = tertiaryKey;
    },

    goBack() {
      if (this.navigationStack.length > 0) {
        const previous = this.navigationStack.pop();
        this.currentLevel = previous.level;
      } else {
        this.currentLevel = 'main';
      }
    },

    getCurrentTitle() {
      if (this.navigationStack.length > 0) {
        return this.navigationStack[this.navigationStack.length - 1].title;
      }
      return '';
    },

    isTertiaryLevel() {
      return this.currentLevel.includes('__');
    },

    getTertiaryInfo() {
      if (this.isTertiaryLevel()) {
        const [brandKey, columnKey] = this.currentLevel.split('__');
        return { brandKey, columnKey };
      }
      return null;
    }
  });

  // Global keyboard shortcut for search
  document.addEventListener('keydown', (e) => {
    // Only trigger if not typing in an input or textarea
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    
    if (e.key === '/') {
      e.preventDefault();
      Alpine.store('search').toggle();
    }
  });

  // Animated Counter Component
  Alpine.data('animatedCounter', () => ({
    displayed: null,
    
    init() {
      // Set initial value
      this.displayed = this.$store.cart.itemCount;
      
      // Watch for changes
      this.$watch('$store.cart.itemCount', (newVal) => {
        // Small delay to allow CSS classes to apply
        setTimeout(() => {
          this.displayed = newVal;
        }, 50);
      });
    }
  }));
});
